import React from 'react';
import styles from './InvoiceCard.module.css';
import Button from '../ui/Button/Button';
import StatusBadge from '../ui/StatusBadge/StatusBadge';
import type { Invoice } from '../../types'; // Corrected import path for Invoice type

interface InvoiceCardProps {
  invoice: Invoice; // Use the shared Invoice type
  onSendReminder: (invoice: Invoice) => void; // Pass full invoice object
  onMarkAsPaid: (invoiceId: string) => void;
  onToggleAutomation: (customerId: string, currentStatus: boolean, customerName: string) => void;
  isAutomationPaused: boolean;
  onSelect: (invoiceId: string) => void; // Pass invoice ID for selecting the card
  isSelected: boolean; // New prop to indicate if the card is selected
  getFollowUpStage: (invoice: Invoice) => string; // New prop for follow-up stage
}

const InvoiceCard: React.FC<InvoiceCardProps> = ({
  invoice,
  onSendReminder,
  onMarkAsPaid,
  onToggleAutomation,
  // @ts-ignore TS6133: 'isAutomationPaused' is declared but its value is never read.
  isAutomationPaused,
  onSelect,
  isSelected,
  getFollowUpStage,
}) => {
  const getDaysOverdue = (dueDate: string) => {
    const now = new Date();
    const due = new Date(dueDate);
    const diffTime = Math.abs(now.getTime() - due.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return due.getTime() < now.getTime() ? diffDays : 0;
  };

  return (
    <div className={`${styles.card} ${isSelected ? styles.selectedCard : ''}`}>
      <div className={styles.header}>
        <input
          type="checkbox"
          checked={isSelected}
          onChange={() => onSelect(invoice.id)}
          className={styles.checkbox}
        />
        <h3 className={styles.customerName}>{invoice.customer_name}</h3>
        <StatusBadge status={invoice.status} />
      </div>
      <div className={styles.details}>
        <p className={styles.amount}>
          <span className={styles.label}>Amount Due:</span>{' '}
          <span className={styles.valueFontMono}>${invoice.amount.toFixed(2)}</span>
        </p>
        <p className={styles.dueDate}>
          <span className={styles.label}>Due Date:</span>{' '}
          {new Date(invoice.due_date).toLocaleDateString()}
        </p>
        {invoice.status === 'Overdue' && (
          <p className={styles.daysOverdue}>
            <span className={styles.label}>Days Overdue:</span>{' '}
            {getDaysOverdue(invoice.due_date)}
          </p>
        )}
        {invoice.last_reminder_sent_at && (
          <p className={styles.lastReminder}>
            <span className={styles.label}>Last Reminder:</span>{' '}
            {new Date(invoice.last_reminder_sent_at).toLocaleDateString()}
          </p>
        )}
        <p className={styles.followUpStage}>
          <span className={styles.label}>Follow-up:</span>{' '}
          {getFollowUpStage(invoice)}
        </p>
      </div>
      <div className={styles.actions}>
        <Button variant="primary" onClick={() => onSendReminder(invoice)}>
          Send Reminder
        </Button>
        <Button variant="success" onClick={() => onMarkAsPaid(invoice.id)} disabled={invoice.status === 'Paid'}>
          Mark as Paid
        </Button>
        {invoice.customers && (
          <Button variant={invoice.customers.automation_paused ? 'warning' : 'secondary'} onClick={() => onToggleAutomation(
            invoice.customer_id,
            invoice.customers!.automation_paused,
            invoice.customers!.name || invoice.customer_name
          )}>
            {invoice.customers.automation_paused ? 'Resume Automation' : 'Pause Automation'}
          </Button>
        )}
      </div>
    </div>
  );
};

export default InvoiceCard;
